import cn from "classnames";
import Typography from "@mui/material/Typography";
import Image from "next/image";
import { useState, useMemo } from "react";

import { DialogWithExit } from "@/src/component/base/DialogWithExit";
import { UnitDetailsLogic } from "@/src/logic";
import { ProviderHooks } from "@/src/hook";
import { StrapiComponentType, DeepSkyType } from "@/src/type";

import styles from "./Pricing.module.scss";

import PreapprovalDialog from "./PreapprovalDialog";
import { BaseButton } from "@/src/component/base/BaseButton";
interface PricingCardProps {
  propertyUnitDetails: StrapiComponentType.Section.PropertyUnitDetails;
  unit: DeepSkyType.AssetType.PropertyInventory.Unit;
  minPricePeriod: string;
  hasFeatures?: boolean;
}

function PricingCard({ propertyUnitDetails, unit, minPricePeriod, hasFeatures }: PricingCardProps) {
  const [featuresAreOpen, setFeaturesAreOpen] = useState(false);
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const [showPreapprovalDialog, setShowPreapprovalDialog] = useState(false);
  const cms = ProviderHooks.useCMS();
  const usePreApproval = !!(propertyUnitDetails.useLighthouse && unit?.fid && unit?.external?.id && unit?.betaEligible);
  const hasPricingTerms = (unit?.prices ?? []).filter((price) => price.pricePeriod === minPricePeriod).length > 1;
  const hasSpecials = (unit?.external?.specials ?? []).length > 0;

  /**
   * open / close features effect
   */
  const handleFeature = () => {
    setFeaturesAreOpen(!featuresAreOpen);
  };

  /**
   * open / close preapproval dialog effect
   */
  const handlePreApproval = () => {
    if (usePreApproval) {
      setShowPreapprovalDialog(!showPreapprovalDialog);
    } else {
      const cta = propertyUnitDetails.tableRowCta;
      const cmsHref = cta?.isExternal ? cta.href : cms.constructLink(cta.href);
      const href = unit?.external?.applyOnlineUrl ? unit.external.applyOnlineUrl : cmsHref;
      window.open(
        href,
        unit?.external?.applyOnlineUrl && typeof unit?.external?.applyOnlineUrl == "string"
          ? "_blank"
          : propertyUnitDetails.tableRowCta?.target
            ? propertyUnitDetails.tableRowCta.target
            : "self"
      );
    }
  };

  /**
   * open / close price dialog effect
   */
  const handlePriceDialog = () => {
    setShowPriceDialog(!showPriceDialog);
  };

  /**
   * get the min price from the unit prices
   */
  const getMinPrice = useMemo(() => {
    return UnitDetailsLogic.getMinPrice(unit, minPricePeriod, propertyUnitDetails);
  }, [unit, minPricePeriod, propertyUnitDetails]);

  /**
   * get the unit amenities from the unit attributes
   */
  const getUnitAmenities = useMemo(() => {
    return UnitDetailsLogic.getUnitAmenities(unit);
  }, [unit]);

  const ollUrl = () => {
    if (!unit?.external?.applyOnlineUrl || typeof unit?.external?.applyOnlineUrl !== "string") {
      return propertyUnitDetails.tableRowCta.href;
    }
    return unit?.external?.applyOnlineUrl;
  };

  return (
    <>
      <div className={styles.card}>
        <div className={styles.row}>
          <div className={cn(styles.group, hasFeatures ? styles.hasFeatures : "")}>
            <Typography
              variant="body1"
              className={styles.item}
            >
              {unit.name}
            </Typography>
            <Typography
              variant="body1"
              className={cn(styles.item, styles.price, hasPricingTerms ? styles.clickable : "")}
              onClick={hasPricingTerms ? handlePriceDialog : undefined}
            >
              {getMinPrice?.labelWithInterval ?? ""}
            </Typography>
            <Typography
              variant="body1"
              className={cn(styles.item, styles.desktopOnly)}
            >
              {UnitDetailsLogic.getAvailableDate(unit, propertyUnitDetails)}
            </Typography>
            {hasFeatures && getUnitAmenities.length > 0 && (
              <div
                className={cn(styles.item, styles.featureTrigger, styles.desktopOnly)}
                onClick={handleFeature}
              >
                <Typography
                  variant="body1"
                  className={styles.item}
                >
                  {propertyUnitDetails.tableFeaturesTitle}
                </Typography>
                <Typography
                  variant="body1"
                  className={styles.highlight}
                >
                  {getUnitAmenities.length}
                </Typography>
                <Image
                  src={"/image/icon_arrow_down_thick_dark.svg"}
                  width={40}
                  height={40}
                  alt="Open Features Icon"
                  className={featuresAreOpen ? styles.upArrow : styles.downArrow}
                />
              </div>
            )}
          </div>
          <div className={styles.end}>
            {propertyUnitDetails.tableRowCta && (
              <BaseButton
                className={styles.applyButton}
                title={propertyUnitDetails.tableRowCta?.label}
                color={propertyUnitDetails.tableRowCta?.color}
                variant={propertyUnitDetails.tableRowCta?.variant}
                onClick={handlePreApproval}
              >
                {propertyUnitDetails.tableRowCta?.label}
              </BaseButton>
            )}
          </div>
        </div>
        {featuresAreOpen && (
          <ul className={cn(styles.features, styles.desktopOnly)}>
            {(getUnitAmenities ?? []).map((amenity, i) => (
              <li
                key={i}
                className={styles.featureLabel}
              >
                {amenity}
              </li>
            ))}
          </ul>
        )}
        {hasSpecials && (
          <div className={styles.specials}>
            <div className={styles.special}>
              <Image
                src={"/image/icon_special.svg"}
                width={20}
                height={20}
                alt="Open Features Icon"
                className={cn(styles.downArrow, styles.a)}
              />
              <Typography
                variant="body1"
                className={cn(styles.label, styles.bold, styles.b)}
              >
                {propertyUnitDetails.specialsAvailableTitle}
              </Typography>
              <Typography
                variant="body1"
                className={cn(styles.label, styles.c)}
              >
                {unit?.external?.specials?.[0]}
              </Typography>
            </div>
          </div>
        )}
        <div className={styles.mobileFooter}>
          <div className={styles.group}>
            <div className={styles.availableDate}>
              <Image
                src={"/image/icon_calendar.svg"}
                width={16}
                height={16}
                alt="Calendar Icon"
              />
              <Typography
                variant="body1"
                className={cn(styles.item)}
              >
                {UnitDetailsLogic.getAvailableDate(unit, propertyUnitDetails)}
              </Typography>
            </div>
            {hasFeatures && (getUnitAmenities ?? []).length > 0 && (
              <div
                className={cn(styles.item, styles.featureTrigger)}
                onClick={handleFeature}
              >
                <Typography
                  variant="body1"
                  className={styles.item}
                >
                  {propertyUnitDetails.tableFeaturesTitle}
                </Typography>
                <Typography
                  variant="body1"
                  className={styles.highlight}
                >
                  {(getUnitAmenities ?? []).length}
                </Typography>
                <Image
                  src={"/image/icon_arrow_down_thick_dark.svg"}
                  width={28}
                  height={28}
                  alt="Open Features Icon"
                  className={featuresAreOpen ? styles.upArrow : styles.downArrow}
                />
              </div>
            )}
          </div>
          {featuresAreOpen && (
            <ul className={styles.features}>
              {(getUnitAmenities ?? []).map((amenity, i) => (
                <li
                  key={i}
                  className={styles.featureLabel}
                >
                  {amenity}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <DialogWithExit
        dialogProps={{
          open: showPriceDialog,
          onClose: handlePriceDialog,
        }}
        dialogContentProps={{
          className: styles.dialogContent,
        }}
        onExit={handlePriceDialog}
      >
        <div className={styles.priceDialog}>
          <Typography
            variant="body1"
            className={styles.title}
          >
            {propertyUnitDetails?.pricingDetailsTitle ?? "Pricing Details"}
          </Typography>
          <Typography
            variant="body1"
            className={styles.sectionTitle}
          >
            {propertyUnitDetails?.bestPriceTitle ?? "Best Price"}
          </Typography>
          <div className={styles.bestPriceContainer}>
            <div className={styles.bestPriceItem}>
              <Typography
                variant="body1"
                className={styles.bestPriceItemHeader}
              >
                {propertyUnitDetails?.bestPriceTitle ?? "Best Price"}
              </Typography>
              <Typography
                variant="body1"
                className={styles.bestPriceItemValue}
              >
                {getMinPrice?.price ?? ""}
              </Typography>
            </div>
            <div className={styles.bestPriceItem}>
              <Typography
                variant="body1"
                className={styles.bestPriceItemHeader}
              >
                {propertyUnitDetails?.leaseTermTitle ?? "Lease Term"}
              </Typography>
              <Typography
                variant="body1"
                className={styles.bestPriceItemValue}
              >
                {UnitDetailsLogic.priceIntervalFormatter(propertyUnitDetails, getMinPrice?.term ?? "")}
              </Typography>
            </div>
          </div>
          <Typography
            variant="body1"
            className={styles.sectionTitle}
          >
            {propertyUnitDetails?.otherPriceOptionsTitle ?? "Other Options"}
          </Typography>
          <div className={styles.otherOptionsContainer}>
            <div className={styles.otherOptionHeader}>
              <Typography
                variant="body1"
                className={styles.bestPriceItemHeader}
              >
                {propertyUnitDetails?.bestPriceTitle ?? "Best Price"}
              </Typography>
              <Typography
                variant="body1"
                className={styles.bestPriceItemHeader}
              >
                {propertyUnitDetails?.leaseTermTitle ?? "Lease Term"}
              </Typography>
            </div>
            {UnitDetailsLogic.getSortedPricesByDuration(unit, minPricePeriod).map((price, i) => (
              <div
                key={i}
                className={styles.otherOption}
              >
                <Typography
                  variant="body1"
                  className={styles.otherOptionText}
                >
                  {UnitDetailsLogic.priceFormatter(price.currency, price.basePrice)}
                </Typography>
                <Typography
                  variant="body1"
                  className={styles.otherOptionText}
                >
                  {UnitDetailsLogic.priceIntervalFormatter(propertyUnitDetails, price.leaseDuration)}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </DialogWithExit>
      {showPreapprovalDialog && (
        <PreapprovalDialog
          key={unit.fid}
          showPreapprovalDialog={showPreapprovalDialog}
          handlePreApproval={handlePreApproval}
          unit={unit}
          ollUrl={ollUrl()}
        />
      )}
    </>
  );
}

export default PricingCard;
